import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Typography, Button, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useDiscoveryQuiz } from '../../contexts/DiscoveryQuizContext';
import { useLocation } from 'react-router-dom';
import { useProfileUpdate } from '../../hooks/useProfileUpdate';

const SESSION_KEY = 'quiz_prompt_dismissed';

/**
 * Non-blocking Snackbar that nudges authenticated users who haven't
 * completed the discovery quiz. Bottom-left so it doesn't overlap the
 * floating chat (bottom-right).
 *
 * **Dismissal is durable** ([[BUGS #185]], owner 2026-08-29: *"dismiss action
 * should be treated as a 'read' indicator for todo checklist and quiz popups,
 * so they don't just keep re-triggering every time user lands"*). This used
 * to remember the dismissal in sessionStorage — which dies with the tab, so
 * "once per session" was "every landing" by another name. The profile flag is
 * the record; sessionStorage stays only as the in-session suppressor while
 * the profile write is in flight.
 */
const PostLoginQuizPrompt: React.FC = () => {
  const { user, userAccount } = useAuth();
  const { hasCompletedQuiz, openQuiz } = useDiscoveryQuiz();
  const { updateProfile } = useProfileUpdate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (
      user &&
      !hasCompletedQuiz &&
      !userAccount?.quizPromptDismissed &&
      pathname !== '/chat' &&
      sessionStorage.getItem(SESSION_KEY) !== 'true'
    ) {
      // Small delay so it doesn't flash on initial load
      const timer = setTimeout(() => setOpen(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [user, userAccount?.quizPromptDismissed, hasCompletedQuiz, pathname]);

  const recordDismissal = () => {
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, 'true'); } catch { /* ignore */ }
    // Fire-and-forget: a failed write costs one extra nudge next visit, and
    // blocking a dismissal on a network round-trip would punish the exact
    // gesture this exists to respect.
    void updateProfile({ quizPromptDismissed: true });
  };

  const handleDismiss = () => {
    recordDismissal();
  };

  const handleTakeQuiz = () => {
    // Opening the quiz IS engaging with the nudge — it must not come back
    // whether or not the quiz gets finished.
    recordDismissal();
    openQuiz('post_login_prompt');
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        severity="info"
        icon={false}
        onClose={handleDismiss}
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          color: 'text.primary',
          '& .MuiAlert-action': { pt: 0 },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Personalize your experience
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Answer 5 quick questions for tailored recommendations
        </Typography>
        <Box>
          <Button
            size="small"
            onClick={handleTakeQuiz}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.primary',
              px: 0,
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Take Quiz
          </Button>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PostLoginQuizPrompt;
